import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { apiGet, apiDelete, Store } from '../../../api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';

interface TimelineSession {
  id: number;
  startTime: string;
  endTime: string;
  scheduledMinutes: number;
  status: string;
  customer: string | null;
  stillcutCount: number;
  startHour: number;
  durationHours: number;
  // 날짜 경계 분할 세션
  isSplit?: boolean;
  splitPart?: 'start' | 'end'; // 'start': 다음날로 이어짐, 'end': 전날에서 이어짐
  originalSessionId?: number;
}

interface RoomTimeline {
  room: {
    id: string;
    name: string;
    roomNumber: number | null;
  };
  sessions: TimelineSession[];
}

interface StoreTimeline {
  store: {
    id: string;
    name: string;
  };
  date: string;
  storeSettings: {
    gameMinutes: number;
    practiceMinutes: number;
    totalSlotMinutes: number;
  };
  roomTimelines: RoomTimeline[];
  operatingHours: {
    excluded_start: string | null;  // 예약 제외 시작 시간
    excluded_end: string | null;    // 예약 제외 종료 시간
    is_closed: boolean;
  } | null;
  summary: {
    totalRooms: number;
    totalSessions: number;
    completed: number;
    inProgress: number;
    scheduled: number;
  };
}

interface EnvironmentLog {
  id: number;
  temperature: number | null;
  humidity: number | null;
  brightness: number | null;
  smoking: boolean | null;
  smokeLevel: number | null;
  recordedAt: string;
}

interface SessionDetail {
  id: number;
  startTime: string;
  endTime: string | null;
  scheduledMinutes: number;
  actualMinutes: number | null;
  status: string;
  price: number | null;
  source: string; // 'kiosk' | 'online'
  // 예약 정보 (온라인 + 키오스크 통합)
  reservationId: number | null;
  reservation: {
    id: number;
    confirmationCode: string;
    customerName: string;
    customerPhone: string;
    customerEmail: string | null;
    gameCount: number;
    status: string;
    payment: {
      id: number;
      amount: number;
      method: string | null;
      methodDetail: string | null;
      pgProvider: string;
      status: string;
      paidAt: string | null;
    } | null;
  } | null;
  customer: {
    name: string;
    phone: string;
  } | null;
  stillcuts: {
    id: number;
    thumbnailUrl: string | null;
    imageUrl: string;
    capturedAt: string;
    captureType: string;
  }[];
  environmentLogs: EnvironmentLog[];
}

interface TimelineTabProps {
  store: Store;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 00:00 ~ 23:00

/** 스틸컷 시점에 가장 가까운 환경 로그 찾기 */
function findClosestEnvLog(capturedAt: string, logs: EnvironmentLog[] | undefined): EnvironmentLog | null {
  if (!logs || logs.length === 0) return null;
  const t = new Date(capturedAt).getTime();
  let closest = logs[0];
  let minDiff = Math.abs(new Date(logs[0].recordedAt).getTime() - t);
  for (let i = 1; i < logs.length; i++) {
    const diff = Math.abs(new Date(logs[i].recordedAt).getTime() - t);
    if (diff < minDiff) {
      minDiff = diff;
      closest = logs[i];
    }
  }
  return minDiff <= 5 * 60 * 1000 ? closest : null; // 5분 이내만
}

/** 환경 모니터링 차트 */
function EnvironmentChart({ logs }: { logs: EnvironmentLog[] }) {
  const chartData = useMemo(() => {
    return logs.map((log) => ({
      time: new Date(log.recordedAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false }),
      temperature: log.temperature != null ? Number(log.temperature) : undefined,
      humidity: log.humidity != null ? Number(log.humidity) : undefined,
      brightness: log.brightness != null ? log.brightness : undefined,
      smokeLevel: log.smokeLevel != null ? log.smokeLevel : undefined,
      smoking: log.smoking === true ? 1 : undefined,
      recordedAt: log.recordedAt,
    }));
  }, [logs]);

  const smokingPoints = useMemo(() => {
    return chartData.filter((d) => d.smoking === 1);
  }, [chartData]);

  const hasTemp = chartData.some((d) => d.temperature !== undefined);
  const hasHumidity = chartData.some((d) => d.humidity !== undefined);
  const hasBrightness = chartData.some((d) => d.brightness !== undefined);
  const hasSmoke = chartData.some((d) => d.smokeLevel !== undefined);

  return (
    <div className="space-y-2">
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <XAxis
              dataKey="time"
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
              tickLine={false}
            />
            <YAxis
              yAxisId="temp"
              tick={{ fontSize: 10 }}
              domain={['auto', 'auto']}
              tickLine={false}
              axisLine={false}
            />
            {hasBrightness && (
              <YAxis
                yAxisId="lux"
                orientation="right"
                tick={{ fontSize: 10 }}
                domain={['auto', 'auto']}
                tickLine={false}
                axisLine={false}
                hide
              />
            )}
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const order = ['temperature', 'humidity', 'brightness', 'smokeLevel'];
                const labels: Record<string, string> = { temperature: '온도', humidity: '습도', brightness: '조도', smokeLevel: '연기' };
                const units: Record<string, string> = { temperature: '°C', humidity: '%', brightness: 'lux', smokeLevel: '' };
                const colors: Record<string, string> = { temperature: '#ef4444', humidity: '#3b82f6', brightness: '#f59e0b', smokeLevel: '#8b5cf6' };
                const sorted = [...payload].sort((a, b) => order.indexOf(a.dataKey as string) - order.indexOf(b.dataKey as string));
                return (
                  <div className="bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden text-xs">
                    <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 font-semibold text-slate-700">{label}</div>
                    <table className="w-full">
                      <tbody>
                        {sorted.map((entry) => {
                          const key = entry.dataKey as string;
                          return (
                            <tr key={key} className="border-b border-slate-100 last:border-0">
                              <td className="px-3 py-1 text-slate-500 whitespace-nowrap">
                                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: colors[key] || '#999' }}></span>
                                {labels[key] || key}
                              </td>
                              <td className="px-3 py-1 text-right font-medium text-slate-800">
                                {entry.value}{units[key] ? <span className="text-slate-400 ml-0.5">{units[key]}</span> : ''}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              }}
            />
            {hasTemp && (
              <Line
                yAxisId="temp"
                type="monotone"
                dataKey="temperature"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
                name="temperature"
                connectNulls
              />
            )}
            {hasHumidity && (
              <Line
                yAxisId="temp"
                type="monotone"
                dataKey="humidity"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                name="humidity"
                connectNulls
              />
            )}
            {hasBrightness && (
              <Line
                yAxisId={hasBrightness ? 'lux' : 'temp'}
                type="monotone"
                dataKey="brightness"
                stroke="#f59e0b"
                strokeWidth={1.5}
                dot={false}
                name="brightness"
                connectNulls
                strokeDasharray="4 2"
              />
            )}
            {hasSmoke && (
              <Line
                yAxisId="temp"
                type="monotone"
                dataKey="smokeLevel"
                stroke="#8b5cf6"
                strokeWidth={1.5}
                dot={false}
                name="smokeLevel"
                connectNulls
                strokeDasharray="2 2"
              />
            )}
            {smokingPoints.map((pt, i) => (
              <ReferenceDot
                key={i}
                x={pt.time}
                y={pt.temperature ?? pt.humidity ?? 0}
                yAxisId="temp"
                r={5}
                fill="#dc2626"
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 px-1">
        {hasTemp && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-red-500 inline-block"></span> 온도 (°C)
          </span>
        )}
        {hasHumidity && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-blue-500 inline-block"></span> 습도 (%)
          </span>
        )}
        {hasBrightness && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-amber-500 inline-block border-dashed"></span> 조도 (lux)
          </span>
        )}
        {hasSmoke && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-violet-500 inline-block border-dashed"></span> 연기
          </span>
        )}
        {smokingPoints.length > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 bg-red-600 rounded-full inline-block"></span> 흡연 감지
          </span>
        )}
      </div>
    </div>
  );
}

// 제외 시간대 배경 오버레이 계산 (제외 시간 = 예약 불가 = 어두운 배경)
const getExcludedOverlayStyle = (operatingHours: StoreTimeline['operatingHours']) => {
  if (!operatingHours || operatingHours.is_closed) return null;

  const { excluded_start, excluded_end } = operatingHours;
  if (!excluded_start || !excluded_end) return null;

  // 시간 파싱 (HH:MM -> 시간 비율 %)
  const parseTime = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return ((h + m / 60) / 24) * 100;
  };

  const startPercent = parseTime(excluded_start);
  const endPercent = parseTime(excluded_end);

  // 일반적인 제외 시간 (예: 09:00 ~ 20:00 = 제외 구간에 어두운 배경)
  if (startPercent < endPercent) {
    return {
      // 제외 구간 (09:00 ~ 20:00 = 예약 불가 = 어두운 배경)
      excluded: { left: `${startPercent}%`, width: `${endPercent - startPercent}%` },
    };
  }

  // 시작과 끝이 같으면 제외 시간 없음
  return null;
};

export default function TimelineTab({ store }: TimelineTabProps) {
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [timeline, setTimeline] = useState<StoreTimeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // 깜빡임 방지용 별도 상태
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [sessionDetail, setSessionDetail] = useState<SessionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState<number | null>(null);
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(null);
  const [resendModal, setResendModal] = useState<{ code: string; phone: string } | null>(null);
  const [resendPhone, setResendPhone] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [stillcutPage, setStillcutPage] = useState(0);
  const STILLCUT_PER_PAGE = 12;
  const dateInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Fetch file size via HEAD when lightbox image changes
  useEffect(() => {
    if (imageModalIndex === null || !sessionDetail) return;
    const sc = sessionDetail.stillcuts[imageModalIndex];
    if (!sc) return;
    setFileSize(null);
    fetch(sc.imageUrl, { method: 'HEAD' })
      .then(r => {
        const cl = r.headers.get('content-length');
        if (cl) setFileSize(parseInt(cl));
      })
      .catch(() => {});
  }, [imageModalIndex, sessionDetail]);

  // Stillcut delete handler
  const handleStillcutDelete = useCallback(async (id: number) => {
    try {
      const response = await apiDelete(`/api/v1/stillcuts/${id}`);
      if (response.success && sessionDetail) {
        const filtered = sessionDetail.stillcuts.filter(sc => sc.id !== id);
        setSessionDetail({ ...sessionDetail, stillcuts: filtered });
        // Adjust lightbox index
        if (imageModalIndex !== null) {
          if (filtered.length === 0) {
            setImageModalIndex(null);
          } else if (imageModalIndex >= filtered.length) {
            setImageModalIndex(filtered.length - 1);
          }
        }
      }
    } catch (error) {
      console.error('Error deleting stillcut:', error);
    } finally {
      setDeleteConfirm(null);
    }
  }, [sessionDetail, imageModalIndex]);

  // Keyboard navigation for lightbox (window listener for reliability)
  useEffect(() => {
    if (imageModalIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (deleteConfirm) {
        if (e.key === 'Enter') { e.preventDefault(); handleStillcutDelete(deleteConfirm); }
        else if (e.key === 'Escape') setDeleteConfirm(null);
        return;
      }
      if (e.key === 'ArrowLeft' && imageModalIndex > 0) {
        setImageModalIndex(imageModalIndex - 1);
        setImageSize(null);
        setFileSize(null);
      } else if (e.key === 'ArrowRight' && sessionDetail && imageModalIndex < sessionDetail.stillcuts.length - 1) {
        setImageModalIndex(imageModalIndex + 1);
        setImageSize(null);
        setFileSize(null);
      } else if (e.key === 'Escape') {
        setImageModalIndex(null);
      } else if (e.key === 'Delete') {
        const sc = sessionDetail?.stillcuts[imageModalIndex];
        if (sc) setDeleteConfirm(sc.id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [imageModalIndex, sessionDetail, deleteConfirm]);

  useEffect(() => {
    loadTimeline();
  }, [store.id, selectedDate]);

  useEffect(() => {
    if (selectedSession) {
      loadSessionDetail(selectedSession);
      setStillcutPage(0);
    } else {
      setSessionDetail(null);
    }
  }, [selectedSession]);

  const loadTimeline = async () => {
    try {
      // 이미 데이터가 있으면 refreshing만 표시 (깜빡임 방지)
      if (timeline) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await apiGet<StoreTimeline>(`/api/v1/stores/${store.id}/timeline?date=${selectedDate}`);
      if (response.success && response.data) {
        setTimeline(response.data);
      }
    } catch (error) {
      console.error('Error loading timeline:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadSessionDetail = async (sessionId: number) => {
    try {
      setDetailLoading(true);
      const response = await apiGet<SessionDetail>(`/api/v1/game-sessions/${sessionId}`);
      if (response.success && response.data) {
        setSessionDetail(response.data);
      }
    } catch (error) {
      console.error('Error loading session detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-500';
      case 'in_progress':
        return 'bg-emerald-500';
      case 'completed':
        return 'bg-slate-400';
      case 'cancelled':
        return 'bg-red-400';
      case 'no_show':
        return 'bg-amber-500';
      default:
        return 'bg-slate-300';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled':
        return '예약됨';
      case 'in_progress':
        return '진행 중';
      case 'completed':
        return '완료';
      case 'cancelled':
        return '취소';
      case 'no_show':
        return '노쇼';
      default:
        return '알 수 없음';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-700';
      case 'in_progress':
        return 'bg-emerald-100 text-emerald-700';
      case 'completed':
        return 'bg-slate-100 text-slate-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'no_show':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const goToDate = (offset: number) => {
    const date = new Date(selectedDate + 'T00:00:00');
    date.setDate(date.getDate() + offset);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    setSelectedDate(`${yyyy}-${mm}-${dd}`);
    setSelectedSession(null);
  };

  const formatLocalDate = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const goToToday = () => {
    setSelectedDate(formatLocalDate(new Date()));
    setSelectedSession(null);
  };

  const isToday = selectedDate === formatLocalDate(new Date());

  const displayDate = useMemo(() => {
    const date = new Date(selectedDate + 'T00:00:00');
    const weekday = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}월 ${day}일 (${weekday})`;
  }, [selectedDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">타임라인 로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">타임라인</h2>
            <p className="text-sm text-slate-500">룸별 사용 현황을 시간대별로 확인합니다</p>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToDate(-1)}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              title="이전 날"
            >
              <i className="ri-arrow-left-s-line text-lg text-slate-600"></i>
            </button>

            <div
              onClick={() => dateInputRef.current?.showPicker?.()}
              className="relative flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
            >
              <i className="ri-calendar-line text-slate-400"></i>
              <span className="text-sm font-medium text-slate-700">{displayDate}</span>
              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedSession(null);
                }}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </div>

            <button
              onClick={() => goToDate(1)}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              title="다음 날"
            >
              <i className="ri-arrow-right-s-line text-lg text-slate-600"></i>
            </button>

            <button
              onClick={goToToday}
              disabled={isToday}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isToday
                  ? 'text-slate-400 bg-slate-100 cursor-default'
                  : 'text-cyan-600 bg-cyan-50 hover:bg-cyan-100 cursor-pointer'
              }`}
            >
              오늘
            </button>

            {/* 새로고침 버튼 */}
            <button
              onClick={() => loadTimeline()}
              disabled={refreshing}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50"
              title="새로고침"
            >
              <i className={`ri-refresh-line text-lg text-slate-600 ${refreshing ? 'animate-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {/* Summary */}
        {timeline && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{timeline.summary.totalSessions}</p>
                <p className="text-xs text-slate-500">전체 세션</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{timeline.summary.inProgress}</p>
                <p className="text-xs text-slate-500">진행 중</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{timeline.summary.scheduled}</p>
                <p className="text-xs text-slate-500">예약됨</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-600">{timeline.summary.completed}</p>
                <p className="text-xs text-slate-500">완료</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">
                  {timeline.storeSettings.gameMinutes}분 + {timeline.storeSettings.practiceMinutes}분
                </p>
                <p className="text-xs text-slate-500">게임 + 연습</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Timeline Grid */}
      <div
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="overflow-x-auto">
          <div className="min-w-[1000px]">
            {/* Time Header */}
            <div className="flex border-b border-slate-200 bg-slate-50">
              <div className="w-24 flex-shrink-0 px-4 py-3 text-sm font-medium text-slate-700 border-r border-slate-200">
                룸
              </div>
              <div className="flex-1 flex">
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    className="flex-1 px-1 py-3 text-center text-xs font-medium text-slate-500 border-r border-slate-100 last:border-r-0"
                  >
                    {hour.toString().padStart(2, '0')}
                  </div>
                ))}
              </div>
            </div>

            {/* Room Rows */}
            {timeline?.roomTimelines.map((roomTimeline, index) => (
              <div
                key={roomTimeline.room.id}
                className={`flex border-b border-slate-100 hover:bg-slate-50/50 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-25'
                }`}
              >
                <div className="w-24 flex-shrink-0 px-4 py-4 text-sm font-medium text-slate-700 border-r border-slate-200">
                  {roomTimeline.room.name}
                </div>
                <div className="flex-1 relative h-16">
                  {/* Hour Grid Lines */}
                  <div className="absolute inset-0 flex">
                    {HOURS.map((hour) => (
                      <div
                        key={hour}
                        className="flex-1 border-r border-slate-100 last:border-r-0"
                      ></div>
                    ))}
                  </div>

                  {/* 제외 시간대 배경 (예약 불가 = 어두운 배경) */}
                  {(() => {
                    const overlay = getExcludedOverlayStyle(timeline?.operatingHours);
                    if (!overlay) return null;
                    return (
                      <>
                        {'excluded' in overlay && (
                          <div
                            className="absolute inset-y-0 bg-slate-200/40 pointer-events-none"
                            style={overlay.excluded}
                          />
                        )}
                      </>
                    );
                  })()}

                  {/* Sessions */}
                  {roomTimeline.sessions.map((session) => {
                    const startHour = session.startHour; // 24시간 기준 (0~23)
                    const leftPercent = (startHour / 24) * 100;
                    const widthPercent = (session.durationHours / 24) * 100;

                    // 분할 세션 스타일
                    const splitBorderStyle = session.isSplit
                      ? session.splitPart === 'start'
                        ? 'border-r-4 border-r-orange-400 rounded-r-none' // 다음날로 이어짐 (오른쪽 절단)
                        : 'border-l-4 border-l-orange-400 rounded-l-none' // 전날에서 이어짐 (왼쪽 절단)
                      : '';

                    const splitTooltip = session.isSplit
                      ? session.splitPart === 'start'
                        ? ' → 다음날로 이어짐'
                        : '전날에서 이어짐 → '
                      : '';

                    return (
                      <div
                        key={`${session.id}-${session.splitPart || 'full'}`}
                        onClick={() => setSelectedSession(session.id === selectedSession ? null : session.id)}
                        className={`absolute top-2 bottom-2 rounded-md cursor-pointer transition-all border-2 border-white/50 ${getStatusColor(
                          session.status
                        )} ${splitBorderStyle} ${
                          selectedSession === session.id
                            ? 'ring-2 ring-offset-1 ring-cyan-500 z-10 brightness-110'
                            : 'hover:brightness-110 hover:shadow-md'
                        }`}
                        style={{
                          left: `${Math.max(0, leftPercent)}%`,
                          width: `${Math.min(widthPercent, 100 - leftPercent)}%`,
                        }}
                        title={`${splitTooltip}${formatTime(session.startTime)} - ${formatTime(session.endTime)}${
                          session.customer ? ` | ${session.customer}` : ''
                        }`}
                      >
                        <div className="px-2 py-1 text-xs text-white truncate">
                          {session.splitPart === 'end' && <span className="opacity-60">← </span>}
                          {formatTime(session.startTime)}
                          {session.customer && (
                            <span className="ml-1 opacity-80">| {session.customer}</span>
                          )}
                          {session.splitPart === 'start' && <span className="opacity-60"> →</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {timeline?.roomTimelines.length === 0 && (
              <div className="px-4 py-12 text-center text-slate-500">
                <i className="ri-door-open-line text-4xl text-slate-300 mb-2"></i>
                <p>등록된 룸이 없습니다</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500"></div>
          <span>예약됨</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-500"></div>
          <span>진행 중</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-slate-400"></div>
          <span>완료</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-400"></div>
          <span>취소</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-500"></div>
          <span>노쇼</span>
        </div>
      </div>

      {/* Session Detail Panel */}
      {selectedSession && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : sessionDetail ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-slate-900">세션 상세</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(sessionDetail.status)}`}>
                    {getStatusLabel(sessionDetail.status)}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">세션 정보</h4>

                  {/* 예약 유형 배지 */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {sessionDetail.source === 'online' || sessionDetail.reservation ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        <i className="ri-global-line text-[10px]"></i> 온라인 예약
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        <i className="ri-store-2-line text-[10px]"></i> 키오스크 현장
                      </span>
                    )}
                    {/* 결제 수단 배지 */}
                    {sessionDetail.reservation?.payment?.method && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        <i className="ri-bank-card-line text-[10px]"></i>
                        {sessionDetail.reservation.payment.methodDetail || sessionDetail.reservation.payment.method}
                      </span>
                    )}
                  </div>

                  {/* 시간 */}
                  <div className="flex items-center gap-3 text-sm">
                    <i className="ri-time-line text-slate-400"></i>
                    <span className="text-slate-700">
                      {formatTime(sessionDetail.startTime)} ~ {sessionDetail.endTime
                        ? formatTime(sessionDetail.endTime)
                        : formatTime(new Date(new Date(sessionDetail.startTime).getTime() + sessionDetail.scheduledMinutes * 60000).toISOString())
                      }
                      {' '}({sessionDetail.actualMinutes ? `${sessionDetail.actualMinutes}분` : `${sessionDetail.scheduledMinutes}분`})
                      {sessionDetail.status === 'in_progress' && ' 진행중'}
                    </span>
                  </div>

                  {/* 예약번호 (통합: reservation.confirmationCode) */}
                  {sessionDetail.reservation?.confirmationCode && (() => {
                    const code = sessionDetail.reservation.confirmationCode;
                    const phone = sessionDetail.reservation?.customerPhone || sessionDetail.customer?.phone || '';
                    return (
                      <div className="flex items-center gap-3 text-sm">
                        <i className="ri-ticket-line text-slate-400"></i>
                        <span className="text-slate-700 font-mono">{code.replace(/(\d{3})(\d{3})/, '$1-$2')}</span>
                        <button
                          onClick={() => { setResendPhone(phone); setResendSent(false); setResendModal({ code, phone }); }}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          title="확인 문자 재전송"
                        >
                          <i className="ri-send-plane-line text-sm"></i>
                        </button>
                      </div>
                    );
                  })()}

                  {/* 고객 정보 */}
                  {(() => {
                    const name = sessionDetail.reservation?.customerName || sessionDetail.customer?.name;
                    const phone = sessionDetail.reservation?.customerPhone || sessionDetail.customer?.phone;
                    if (!name && !phone) return null;
                    return (
                      <>
                        {name && (
                          <div className="flex items-center gap-3 text-sm">
                            <i className="ri-user-line text-slate-400"></i>
                            <span className="text-slate-700">{name}</span>
                          </div>
                        )}
                        {phone && (
                          <div className="flex items-center gap-3 text-sm">
                            <i className="ri-phone-line text-slate-400"></i>
                            <span className="text-slate-700">{phone}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* 결제 정보 */}
                  {(() => {
                    // 온라인: reservation.payment에서, 키오스크: session 직접 필드에서
                    const pay = sessionDetail.reservation?.payment;
                    const amount = pay ? Number(pay.amount) : sessionDetail.price ? Number(sessionDetail.price) : null;
                    const method = pay?.method;
                    const detail = pay?.methodDetail;
                    const paidAt = pay?.paidAt;

                    if (!amount && !method) return null;

                    return (
                      <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
                        {amount != null && (
                          <div className="flex items-center gap-3 text-sm">
                            <i className="ri-money-dollar-circle-line text-slate-400"></i>
                            <span className="text-slate-700 font-semibold">{amount.toLocaleString()}원</span>
                          </div>
                        )}
                        {method && (
                          <div className="flex items-center gap-3 text-sm">
                            <i className="ri-bank-card-line text-slate-400"></i>
                            <span className="text-slate-700">
                              {method}
                              {detail && <span className="text-slate-500 ml-1">({detail})</span>}
                            </span>
                          </div>
                        )}
                        {paidAt && (
                          <div className="flex items-center gap-3 text-sm">
                            <i className="ri-calendar-check-line text-slate-400"></i>
                            <span className="text-slate-500">
                              {new Date(paidAt).toLocaleString('ko-KR', {
                                month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Environment Chart */}
                <div className="md:col-span-3">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">환경 모니터링</h4>
                  {sessionDetail.environmentLogs && sessionDetail.environmentLogs.length > 0 ? (
                    <EnvironmentChart logs={sessionDetail.environmentLogs} />
                  ) : (
                    <div className="text-sm text-slate-400 bg-slate-50 rounded-lg p-6 text-center">
                      <i className="ri-line-chart-line text-2xl mb-1"></i>
                      <p>환경 데이터 없음</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Stillcuts (paginated, 1-row horizontal) */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                {(() => {
                  const totalSc = sessionDetail.stillcuts.length;
                  const totalPages = Math.ceil(totalSc / STILLCUT_PER_PAGE);
                  const currentPage = stillcutPage + 1;
                  const pageItems = sessionDetail.stillcuts.slice(
                    stillcutPage * STILLCUT_PER_PAGE,
                    (stillcutPage + 1) * STILLCUT_PER_PAGE
                  );

                  return (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">
                          <i className="ri-camera-line mr-2"></i>
                          스틸컷 ({totalSc}장)
                        </h4>
                        {totalPages > 1 && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setStillcutPage(p => Math.max(0, p - 1))}
                              disabled={stillcutPage === 0}
                              className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                            >
                              <i className="ri-arrow-left-s-line"></i>
                            </button>
                            <span className="text-xs text-slate-500 px-1">{currentPage}/{totalPages}</span>
                            <button
                              onClick={() => setStillcutPage(p => Math.min(totalPages - 1, p + 1))}
                              disabled={currentPage >= totalPages}
                              className="px-2 py-1 text-xs rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                            >
                              <i className="ri-arrow-right-s-line"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      {totalSc > 0 ? (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {pageItems.map((stillcut, idx) => {
                            const globalIdx = stillcutPage * STILLCUT_PER_PAGE + idx;
                            return (
                              <div
                                key={stillcut.id}
                                className="group relative flex-shrink-0 w-36 bg-slate-50 rounded-lg overflow-hidden border border-slate-200 hover:border-cyan-300 transition-colors cursor-pointer"
                                onClick={() => { setImageModalIndex(globalIdx); setImageSize(null); setFileSize(null); }}
                              >
                                <div className="aspect-video">
                                  <img
                                    src={stillcut.thumbnailUrl || stillcut.imageUrl}
                                    alt={`스틸컷 ${stillcut.id}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                                <div className="px-1.5 py-1">
                                  <span className="text-[10px] text-slate-600">{formatTime(stillcut.capturedAt)}</span>
                                </div>
                                {/* Hover zoom icon */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none">
                                  <i className="ri-zoom-in-line text-lg text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg"></i>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400 bg-slate-50 rounded-lg p-4 text-center">
                          <i className="ri-image-line text-2xl mb-1"></i>
                          <p>스틸컷이 없습니다</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-500 py-8">
              세션 정보를 불러올 수 없습니다
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-slate-900 mb-2">스틸컷 삭제</h3>
            <p className="text-sm text-slate-600 mb-6">이 스틸컷을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                취소
              </button>
              <button
                autoFocus
                onClick={() => handleStillcutDelete(deleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox (CctvTab 스타일 통일) */}
      {imageModalIndex !== null && sessionDetail && sessionDetail.stillcuts.length > 0 && (() => {
        const stillcut = sessionDetail.stillcuts[imageModalIndex];
        if (!stillcut) return null;

        const hasPrev = imageModalIndex > 0;
        const hasNext = imageModalIndex < sessionDetail.stillcuts.length - 1;
        const capturedAt = new Date(stillcut.capturedAt);
        const dateTimeStr = capturedAt.toLocaleString('ko-KR', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        });

        const handleDownload = (e: React.MouseEvent) => {
          e.stopPropagation();
          const a = document.createElement('a');
          a.href = stillcut.imageUrl;
          a.download = stillcut.imageUrl.split('/').pop() || 'stillcut.jpg';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        };

        return (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setImageModalIndex(null)}
          >
            {/* Close */}
            <button
              onClick={() => setImageModalIndex(null)}
              className="absolute top-4 right-4 p-2 text-white/70 hover:text-white z-10"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>

            {/* Prev */}
            <button
              onClick={(e) => { e.stopPropagation(); if (hasPrev) { setImageModalIndex(imageModalIndex - 1); setImageSize(null); setFileSize(null); } }}
              disabled={!hasPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full disabled:opacity-20 transition-all z-10"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>

            {/* Next */}
            <button
              onClick={(e) => { e.stopPropagation(); if (hasNext) { setImageModalIndex(imageModalIndex + 1); setImageSize(null); setFileSize(null); } }}
              disabled={!hasNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 text-white rounded-full disabled:opacity-20 transition-all z-10"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>

            {/* Image + Info */}
            <div
              className="flex flex-col items-center max-w-5xl max-h-[90vh] w-full px-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={stillcut.imageUrl}
                alt="스틸컷 확대"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
                }}
              />

              {/* Info bar */}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/80">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {dateTimeStr}
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20">
                  {stillcut.captureType === 'periodic' ? '주기적' : stillcut.captureType === 'test' ? '테스트' : stillcut.captureType}
                </span>
                {(() => {
                  const env = findClosestEnvLog(stillcut.capturedAt, sessionDetail.environmentLogs);
                  if (!env) return null;
                  return (
                    <span className="flex items-center gap-2 text-xs text-white/60">
                      {env.temperature != null && <span>{Number(env.temperature)}°C</span>}
                      {env.humidity != null && <span>{Number(env.humidity)}%</span>}
                      {env.brightness != null && <span>{env.brightness}lux</span>}
                      {env.smokeLevel != null && <span>연기:{env.smokeLevel}</span>}
                      {env.smoking && <span className="text-red-400">흡연</span>}
                    </span>
                  );
                })()}
                {imageSize && (
                  <span className="flex items-center gap-1.5 text-white/50">
                    <i className="ri-image-line"></i>
                    {imageSize.w} x {imageSize.h}
                  </span>
                )}
                {fileSize && (
                  <span className="flex items-center gap-1.5 text-white/50">
                    <i className="ri-file-line"></i>
                    {formatFileSize(fileSize)}
                  </span>
                )}
                <span className="text-white/50 ml-auto">
                  {imageModalIndex + 1} / {sessionDetail.stillcuts.length}
                </span>
              </div>

              {/* Action buttons */}
              <div className="mt-2 flex items-center gap-3">
                <button
                  onClick={handleDownload}
                  className="px-3 py-1.5 text-xs text-cyan-300 hover:text-cyan-200 hover:bg-cyan-900/30 rounded-lg transition-colors"
                >
                  <i className="ri-download-line mr-1"></i>다운로드
                </button>
                <button
                  onClick={() => setDeleteConfirm(stillcut.id)}
                  className="px-3 py-1.5 text-xs text-red-300 hover:text-red-200 hover:bg-red-900/30 rounded-lg transition-colors"
                >
                  <i className="ri-delete-bin-line mr-1"></i>삭제
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 확인 문자 재전송 모달 */}
      {resendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setResendModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">확인 문자 재전송</h3>
              <button onClick={() => setResendModal(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-xs text-blue-500 mb-1">예약번호</div>
              <div className="text-2xl font-bold font-mono text-blue-700 tracking-wider">
                {resendModal.code.replace(/(\d{3})(\d{3})/, '$1-$2')}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">수신 휴대폰번호</label>
              <input
                type="tel"
                value={resendPhone}
                onChange={(e) => { setResendPhone(e.target.value); setResendSent(false); }}
                placeholder="010-0000-0000"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {resendModal.phone && resendPhone !== resendModal.phone && (
                <p className="text-xs text-amber-600 mt-1">
                  <i className="ri-alert-line mr-1"></i>
                  원래 번호: {resendModal.phone}
                </p>
              )}
            </div>

            {resendSent ? (
              <div className="text-center py-2">
                <i className="ri-check-line text-green-500 text-2xl"></i>
                <p className="text-sm text-green-600 mt-1">전송 완료 (연동 준비 중)</p>
              </div>
            ) : (
              <button
                onClick={() => setResendSent(true)}
                disabled={!resendPhone.trim()}
                className="w-full py-2.5 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-send-plane-line mr-2"></i>
                전송
              </button>
            )}

            <p className="text-xs text-slate-400 text-center mt-3">
              카카오톡/SMS 연동 후 실제 전송됩니다
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
